/**
 * Client-side mirror of the backend SurveyVisibilityEngine. It only drives the
 * live show/hide in the runner — the server always re-validates on submit, so a
 * tampered client can never bypass required questions or sneak hidden answers in.
 */

function isAnswered(v) {
  if (v === null || v === undefined) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'string') return v.trim() !== '';
  return true;
}

function toList(v) {
  if (Array.isArray(v)) return v;
  return v === null || v === undefined ? [] : [v];
}

function looseEquals(a, b) {
  if (Array.isArray(a) || Array.isArray(b)) {
    if (Array.isArray(a) && Array.isArray(b)) {
      const sa = a.map(String).sort();
      const sb = b.map(String).sort();
      return JSON.stringify(sa) === JSON.stringify(sb);
    }
    return false;
  }
  return String(a) === String(b);
}

function anyMatch(answer, values) {
  const needles = values.map(String);
  if (Array.isArray(answer)) return answer.some((x) => needles.includes(String(x)));
  return needles.includes(String(answer));
}

function contains(answer, value) {
  const needle = String(value);
  if (Array.isArray(answer)) return answer.map(String).includes(needle);
  return String(answer) === needle;
}

function compare(a, b) {
  const na = Number(a);
  const nb = Number(b);
  if (a !== '' && b !== '' && !Number.isNaN(na) && !Number.isNaN(nb)) {
    return na < nb ? -1 : na > nb ? 1 : 0;
  }
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (!Number.isNaN(ta) && !Number.isNaN(tb)) return ta < tb ? -1 : ta > tb ? 1 : 0;
  return 2; // incomparable
}

function between(answer, value) {
  const bounds = toList(value);
  if (bounds.length !== 2) return false;
  let [min, max] = bounds;
  if (compare(min, max) === 1) [min, max] = [max, min];
  return [0, 1].includes(compare(answer, min)) && [0, -1].includes(compare(answer, max));
}

function evalCondition(op, answer, value, answered, sourceVisible) {
  if (!sourceVisible) return false;
  if (op === 'answered') return answered;
  if (op === 'not_answered') return !answered;
  if (!answered) return false;
  switch (op) {
    case 'equals': return looseEquals(answer, value);
    case 'not_equals': return !looseEquals(answer, value);
    case 'in': return anyMatch(answer, toList(value));
    case 'not_in': return !anyMatch(answer, toList(value));
    case 'contains': return contains(answer, value);
    case 'gt': return compare(answer, value) === 1;
    case 'gte': return [0, 1].includes(compare(answer, value));
    case 'lt': return compare(answer, value) === -1;
    case 'lte': return [0, -1].includes(compare(answer, value));
    case 'between': return between(answer, value);
    default: return false;
  }
}

export function resolveVisibility(questions, answers) {
  const visible = {};
  questions.forEach((q) => {
    visible[q.id] = !q.rule;
  });

  const maxPasses = questions.length + 1;
  for (let pass = 0; pass < maxPasses; pass += 1) {
    let changed = false;
    questions.forEach((q) => {
      if (!q.rule) return;
      const conditions = q.rule.conditions || [];
      const results = conditions.map((c) => {
        const src = c.source_question_id;
        const sourceVisible = src != null && !!visible[src];
        const answered = sourceVisible && answers[src] !== undefined && isAnswered(answers[src]);
        const ans = answered ? answers[src] : null;
        return evalCondition(c.operator, ans, c.value, answered, sourceVisible);
      });
      const logic = (q.rule.logic || 'AND').toUpperCase();
      const res = conditions.length === 0
        ? true
        : (logic === 'OR' ? results.includes(true) : !results.includes(false));
      if (res !== visible[q.id]) {
        visible[q.id] = res;
        changed = true;
      }
    });
    if (!changed) break;
  }
  return visible;
}

export { isAnswered };
